import { useMemo, useState } from 'react'
import { Button, Input, Select } from '@/shared/ui'
import { toast } from '@/shared/model/toast'
import {
  integrationsApi,
  useAction,
  type Catalog,
  type Delivery,
  type Webhook,
  type WebhookInput,
} from '@/entities/integration'
import {
  AuthEditor,
  BitrixLogo,
  ChoiceCard,
  ConditionsEditor,
  CopyField,
  Drawer,
  JsonView,
  Label,
  PlayIcon,
  PlusIcon,
  SendIcon,
  Step,
  KVEditor,
  timeAgo,
  useInsert,
  useSecrets,
  VariableChips,
  CheckIcon,
} from './kit'
import { BitrixBuilder, countSteps, emptyFlow, parseFlow, type BitrixFlow } from './BitrixBuilder'
import { DeliveryDetails } from './Journal'
import s from './integrations.module.css'

/* --------------------------------------------------------------- List */

export function OutgoingTab({
  catalog,
  hooks,
  onOpenJournal,
}: {
  catalog: Catalog
  hooks: Webhook[]
  onOpenJournal: (webhookId: number) => void
}) {
  const [editing, setEditing] = useState<{ hook: Webhook | null; kind: 'http' | 'bitrix'; template?: string } | null>(null)
  const titles = useMemo(() => Object.fromEntries(catalog.events.map((e) => [e.type, e.title])), [catalog])

  return (
    <>
      <div className={s.sectionHead}>
        <div>
          <h2 className={s.sectionTitle}>Отправка в CRM</h2>
          <p className={s.sectionText}>
            Случилось событие — звонок завершён, клиент стал горячим, обзвон закончился — и мы сразу отправляем его в вашу
            систему или делаем нужные шаги в Битриксе.
          </p>
        </div>
        {hooks.length > 0 && (
          <Button onClick={() => setEditing({ hook: null, kind: 'http' })}>
            <span className={s.btnRow}><PlusIcon /> Добавить</span>
          </Button>
        )}
      </div>

      {hooks.length === 0 ? (
        <div className={s.starters}>
          <button type="button" className={s.starter} onClick={() => setEditing({ hook: null, kind: 'bitrix' })}>
            <span className={`${s.cardIcon} ${s.cardIconBitrix}`}>
              <BitrixLogo />
            </span>
            <span className={s.starterTitle}>Битрикс24</span>
            <span className={s.starterText}>Итоги звонков в карточку, стадии, дела менеджерам. Каждый шаг включаете сами.</span>
          </button>
          <button type="button" className={s.starter} onClick={() => setEditing({ hook: null, kind: 'http' })}>
            <span className={`${s.cardIcon} ${s.cardIconBrand}`}>
              <SendIcon />
            </span>
            <span className={s.starterTitle}>Свой сервер (вебхук)</span>
            <span className={s.starterText}>Отправляем JSON на ваш адрес. Выбираете события, формат и авторизацию.</span>
          </button>
          {catalog.bitrixTemplates.slice(0, 2).map((t) => (
            <button key={t.key} type="button" className={s.starter}
              onClick={() => setEditing({ hook: null, kind: 'bitrix', template: t.key })}>
              <span className={`${s.cardIcon} ${s.cardIconBitrix}`}>
                <BitrixLogo />
              </span>
              <span className={s.starterTitle}>{t.title}</span>
              <span className={s.starterText}>{t.description}</span>
            </button>
          ))}
        </div>
      ) : (
        <div className={s.cards}>
          {hooks.map((h) => (
            <HookCard key={h.id} hook={h} titles={titles} onOpen={() => setEditing({ hook: h, kind: h.bodyMode === 'bitrix' ? 'bitrix' : 'http' })}
              onJournal={() => onOpenJournal(h.id)} />
          ))}
          <button type="button" className={s.addCard} onClick={() => setEditing({ hook: null, kind: 'http' })}>
            <span className={s.addCardIcon}>
              <PlusIcon />
            </span>
            Новое уведомление
          </button>
        </div>
      )}

      {editing && (
        <WebhookEditor
          catalog={catalog}
          hook={editing.hook}
          initialKind={editing.kind}
          templateKey={editing.template}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  )
}

function HookCard({
  hook,
  titles,
  onOpen,
  onJournal,
}: {
  hook: Webhook
  titles: Record<string, string>
  onOpen: () => void
  onJournal: () => void
}) {
  const toggle = useAction((on: boolean) => integrationsApi.setWebhookEnabled(hook.id, on), (r) =>
    r.enabled ? 'Включено' : 'Выключено',
  )
  const bitrix = hook.bodyMode === 'bitrix'
  const st = hook.stats
  const steps = bitrix ? countSteps(parseFlow(hook.bodyTemplate)) : 0
  return (
    <div className={`${s.card} ${hook.enabled ? '' : s.cardOff}`} onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}>
      <div className={s.cardTop}>
        <span className={`${s.cardIcon} ${bitrix ? s.cardIconBitrix : s.cardIconBrand}`}>
          {bitrix ? <BitrixLogo /> : <SendIcon />}
        </span>
        <div className={s.cardTitles}>
          <h3 className={s.cardTitle}>{hook.name}</h3>
          <div className={s.cardSub}>{bitrix ? `Битрикс24 · шагов: ${steps}` : `${hook.method} ${hook.url}`}</div>
        </div>
        <span onClick={(e) => e.stopPropagation()}>
          <label className={s.switch} title={hook.enabled ? 'Выключить' : 'Включить'}>
            <input type="checkbox" checked={hook.enabled} disabled={toggle.isPending}
              onChange={(e) => toggle.mutate(e.target.checked)} />
            <span className={s.switchTrack}>
              <span className={s.switchThumb} />
            </span>
          </label>
        </span>
      </div>
      {hook.pausedReason && (
        <div className={`${s.alert} ${s.alertBad}`}>
                    <span>{hook.pausedReason}</span>
        </div>
      )}
      <div className={s.cardChips}>
        {hook.events.slice(0, 3).map((e) => (
          <span key={e} className={s.chip}>
            {titles[e] ?? e}
          </span>
        ))}
        {hook.events.length > 3 && <span className={s.chip}>+{hook.events.length - 3}</span>}
        {hook.filters.length > 0 && <span className={`${s.chip} ${s.chipBrand}`}>{hook.filters.length === 1 ? 'с условием' : `условий: ${hook.filters.length}`}</span>}
      </div>
      <div className={s.cardFoot}>
        <span>
          {st.success + st.failed + st.pending > 0 ? `доставлено ${st.success}` : ''}
          {st.failed > 0 && <b style={{ color: 'var(--danger)' }}> · ошибок {st.failed}</b>}
          {st.pending > 0 && ` · в очереди ${st.pending}`}
        </span>
        <span>{st.lastAt ? timeAgo(st.lastAt) : 'ещё не отправляли'}</span>
        <button type="button" className={s.addLink} onClick={(e) => (e.stopPropagation(), onJournal())}>
          Журнал
        </button>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------- Editor */

function WebhookEditor({
  catalog,
  hook,
  initialKind,
  templateKey,
  onClose,
}: {
  catalog: Catalog
  hook: Webhook | null
  initialKind: 'http' | 'bitrix'
  templateKey?: string
  onClose: () => void
}) {
  const tpl = catalog.bitrixTemplates.find((t) => t.key === templateKey)
  const [kind, setKind] = useState<'http' | 'bitrix'>(initialKind)
  const [form, setForm] = useState<WebhookInput>(() =>
    hook
      ? {
          name: hook.name,
          enabled: hook.enabled,
          method: hook.method,
          url: hook.url,
          headers: hook.headers ?? [],
          auth: hook.auth,
          secrets: {},
          events: hook.events,
          filters: hook.filters ?? [],
          bodyMode: hook.bodyMode,
          bodyTemplate: hook.bodyTemplate,
        }
      : {
          name: tpl ? tpl.title : initialKind === 'bitrix' ? 'Битрикс24' : 'Мой сервер',
          enabled: true,
          method: 'POST',
          url: '',
          headers: [],
          auth: { kind: initialKind === 'bitrix' ? 'bitrix' : 'none' },
          secrets: {},
          events: tpl ? tpl.events : ['call.completed'],
          filters: tpl?.filters ?? [],
          bodyMode: initialKind === 'bitrix' ? 'bitrix' : 'standard',
          bodyTemplate: '',
        },
  )
  const [flow, setFlow] = useState<BitrixFlow>(() =>
    hook?.bodyMode === 'bitrix' ? parseFlow(hook.bodyTemplate) : tpl ? parseFlow(tpl.flow) : emptyFlow(),
  )
  const { secrets, setSecret } = useSecrets()
  const [sampleOf, setSampleOf] = useState<string | null>(null)
  const [testEvent, setTestEvent] = useState(form.events[0] ?? 'test.ping')
  const [testResult, setTestResult] = useState<Delivery | null>(null)

  const set = <K extends keyof WebhookInput>(k: K, v: WebhookInput[K]) => setForm((f) => ({ ...f, [k]: v }))

  const switchKind = (k: 'http' | 'bitrix') => {
    setKind(k)
    // Имя по умолчанию следует за типом, своё имя клиента не трогаем.
    const rename = (name: string, to: string) => (name === 'Мой сервер' || name === 'Битрикс24' || !name.trim() ? to : name)
    if (k === 'bitrix') setForm((f) => ({ ...f, name: rename(f.name, 'Битрикс24'), bodyMode: 'bitrix', auth: { kind: 'bitrix' }, url: '' }))
    else setForm((f) => ({ ...f, name: rename(f.name, 'Мой сервер'), bodyMode: 'standard', auth: { kind: 'none' } }))
  }

  const payload = (): WebhookInput => {
    const sec: Record<string, string> = {}
    for (const [k, v] of Object.entries(secrets)) if (v) sec[k] = v
    return {
      ...form,
      secrets: sec,
      bodyTemplate: kind === 'bitrix' ? JSON.stringify(flow) : form.bodyTemplate,
      bodyMode: kind === 'bitrix' ? 'bitrix' : form.bodyMode,
      auth: kind === 'bitrix' ? { kind: 'bitrix' } : form.auth,
    }
  }

  const save = useAction(() => integrationsApi.saveWebhook(hook?.id ?? null, payload()), hook ? 'Сохранено' : 'Готово! Уведомления включены')
  const remove = useAction(() => integrationsApi.deleteWebhook(hook!.id), 'Удалено')
  const rotate = useAction(() => integrationsApi.rotateWebhook(hook!.id), 'Новый ключ подписи создан')
  const test = useAction(() => integrationsApi.testWebhook(hook?.id ?? null, testEvent, payload()))
  const oauth = useAction(() => integrationsApi.oauthStart('webhook', hook!.id))

  const groups = useMemo(() => {
    const g = new Map<string, Catalog['events']>()
    for (const e of catalog.events) {
      if (e.type === 'test.ping') continue
      g.set(e.group, [...(g.get(e.group) ?? []), e])
    }
    return [...g.entries()]
  }, [catalog])

  const selectedEvents = catalog.events.filter((e) => form.events.includes(e.type))
  const variables = useMemo(() => {
    const seen = new Map<string, (typeof catalog.events)[number]['variables'][number]>()
    for (const e of selectedEvents.length ? selectedEvents : catalog.events.filter((x) => x.type === 'call.completed'))
      for (const v of e.variables) if (!seen.has(v.path)) seen.set(v.path, v)
    return [...seen.values()]
  }, [selectedEvents, catalog])

  const filterFields = [
    { value: 'data.contact.label', label: 'Оценка клиента (hot, interested, thinking, cold, negative, do_not_call)' },
    { value: 'data.report.outcome', label: 'Результат звонка' },
    { value: 'data.call.campaign.name', label: 'Название обзвона' },
    { value: 'data.call.bot.name', label: 'Бот' },
    { value: 'data.call.durationSec', label: 'Длительность, сек' },
    { value: 'data.report.score', label: 'Оценка звонка' },
    ...variables
      .filter((v) => !['data.contact.label', 'data.report.outcome', 'data.call.campaign.name', 'data.call.bot.name', 'data.call.durationSec', 'data.report.score'].includes(v.path))
      .map((v) => ({ value: v.path, label: v.path })),
  ]

  const toggleEvent = (t: string) =>
    set('events', form.events.includes(t) ? form.events.filter((x) => x !== t) : [...form.events, t])

  const doSave = () =>
    save.mutate(undefined, {
      onSuccess: () => onClose(),
    })

  const doTest = () => {
    setTestResult(null)
    test.mutate(undefined, { onSuccess: (d) => setTestResult(d) })
  }

  const footer = (
    <>
      {hook && (
        <Button variant="ghost" onClick={() => window.confirm(`Удалить «${hook.name}»? Журнал отправок тоже удалится.`) && remove.mutate(undefined, { onSuccess: onClose })}
          loading={remove.isPending}>
          Удалить
        </Button>
      )}
      <span className={s.spacer} />
      <Button variant="secondary" onClick={onClose}>
        Отмена
      </Button>
      <Button onClick={doSave} loading={save.isPending} disabled={form.events.length === 0}>
        <span className={s.btnRow}><CheckIcon /> {hook ? 'Сохранить' : 'Сохранить и включить'}</span>
      </Button>
    </>
  )

  let n = 1
  return (
    <Drawer
      title={hook ? hook.name : kind === 'bitrix' ? 'Действия в Битриксе' : 'Новое уведомление'}
      subtitle={kind === 'bitrix' ? 'Каждый шаг включаете сами — без галочки мы в Битриксе ничего не трогаем' : 'Сообщаем вашей системе о событиях'}
      icon={kind === 'bitrix' ? <BitrixLogo size={24} /> : <SendIcon />}
      onClose={onClose}
      footer={footer}
      wide
    >
      {!hook && (
        <div className={s.choices}>
          <ChoiceCard active={kind === 'bitrix'} onClick={() => switchKind('bitrix')} icon={<BitrixLogo />} title="Битрикс24"
            text="Шаги в CRM: карточка, стадия, поля, дела" />
          <ChoiceCard active={kind === 'http'} onClick={() => switchKind('http')} icon={<SendIcon />} title="Свой сервер"
            text="JSON на ваш адрес: amoCRM, 1С, свой бэкенд" />
        </div>
      )}

      <Step n={n++} title="Название" hint="Чтобы потом узнать в списке и журнале.">
        <Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Например: Горячие → отдел продаж" />
      </Step>

      <Step n={n++} title="Когда отправлять" hint="Отметьте события. «Пример» покажет, что именно придёт.">
        <div className={s.eventGroups}>
          {groups.map(([group, list]) => {
            const allOn = list.every((e) => form.events.includes(e.type))
            return (
              <div key={group} className={s.eventGroup}>
                <div className={s.eventGroupHead}>
                  {group}
                  <button type="button" className={s.eventGroupAll}
                    onClick={() =>
                      set('events', allOn
                        ? form.events.filter((x) => !list.some((e) => e.type === x))
                        : [...new Set([...form.events, ...list.map((e) => e.type)])])
                    }>
                    {allOn ? 'снять все' : 'выбрать все'}
                  </button>
                </div>
                {list.map((e) => {
                  const on = form.events.includes(e.type)
                  return (
                    <div key={e.type} className={`${s.eventItem} ${on ? s.eventItemOn : ''}`} onClick={() => toggleEvent(e.type)}>
                      <span className={`${s.check} ${on ? s.checkOn : ''}`}>{on && <CheckIcon />}</span>
                      <div className={s.eventText}>
                        <div className={s.eventTitle}>{e.title}</div>
                        <div className={s.eventDesc}>{e.description}</div>
                      </div>
                      <button type="button" className={s.eventSampleBtn}
                        onClick={(ev) => (ev.stopPropagation(), setSampleOf(sampleOf === e.type ? null : e.type))}>
                        {sampleOf === e.type ? 'скрыть' : 'пример'}
                      </button>
                    </div>
                  )
                })}
              </div>
            )
          })}
        </div>
        {sampleOf && (
          <div className={s.formRow}>
            <Label>Пример: {catalog.events.find((e) => e.type === sampleOf)?.title}</Label>
            <JsonView value={catalog.events.find((e) => e.type === sampleOf)?.sample} maxHeight={300} />
          </div>
        )}
        {form.events.length === 0 && <div className={s.alert}>Выберите хотя бы одно событие</div>}
      </Step>

      <Step n={n++} title="Условия" hint="Необязательно. Например, отправлять только горячих или только один обзвон.">
        <ConditionsEditor
          items={form.filters}
          onChange={(v) => set('filters', v)}
          fields={filterFields}
          operators={catalog.operators}
          emptyText="Без условий — отправляем каждое выбранное событие"
          presets={[
            { label: 'только горячие', cond: { path: 'data.contact.label', op: 'eq', value: 'hot' } },
            { label: 'только дозвонились', cond: { path: 'data.call.durationSec', op: 'gt', value: '10' } },
            ...[...new Map(catalog.campaigns.map((c) => [c.name, c])).values()].slice(0, 2).map((c) => ({
              label: `обзвон «${c.name}»`,
              cond: { path: 'data.call.campaign.name', op: 'eq', value: c.name },
            })),
          ]}
        />
      </Step>

      {kind === 'bitrix' ? (
        <Step n={n++} title="Что сделать в Битриксе" hint="Подключите портал и включите нужные шаги.">
          <BitrixBuilder
            webhookId={hook?.id ?? null}
            flow={flow}
            setFlow={setFlow}
            secrets={secrets}
            setSecret={setSecret}
            hints={hook?.secrets ?? {}}
            catalog={catalog}
            event={form.events[0] ?? 'call.completed'}
            variables={variables}
          />
        </Step>
      ) : (
        <>
          <Step n={n++} title="Куда отправлять">
            <div className={s.urlRow}>
              <Select value={form.method} onChange={(e) => set('method', e.target.value)}
                options={['POST', 'PUT', 'PATCH'].map((m) => ({ value: m, label: m }))} />
              <Input value={form.url} onChange={(e) => set('url', e.target.value)} placeholder="https://api.ваша-crm.ru/hooks/calls" />
            </div>
            <details>
              <summary className={s.addLink} style={{ cursor: 'pointer' }}>Свои заголовки</summary>
              <div style={{ marginTop: 8 }}>
                <KVEditor items={form.headers} onChange={(v) => set('headers', v)} />
              </div>
            </details>
          </Step>

          <Step n={n++} title="Авторизация" hint="Как ваш сервер узнает, что запрос от нас. Секреты храним зашифрованными.">
            <AuthEditor
              auth={form.auth}
              setAuth={(a) => set('auth', a)}
              secrets={secrets}
              setSecret={setSecret}
              hints={hook?.secrets ?? {}}
              kinds={catalog.authKinds.filter((k) => k !== 'bitrix')}
              onConnect={hook ? () => oauth.mutate(undefined, { onSuccess: (r) => (window.location.href = r.url) }) : undefined}
            />
            {hook?.signingSecret && (
              <div className={s.formRow}>
                <Label hint="Каждый запрос подписан: X-Synta-Signature: t=<время>,v1=HMAC-SHA256(«время.тело», ключ). По X-Synta-Event-Id отсекайте повторы.">
                  Ключ подписи
                </Label>
                <div className={s.condActions}>
                  <div style={{ flex: 1 }}>
                    <CopyField value={hook.signingSecret} secret label="Ключ подписи" />
                  </div>
                  <Button size="sm" variant="secondary" loading={rotate.isPending}
                    onClick={() => window.confirm('Создать новый ключ? Старый перестанет подходить сразу.') && rotate.mutate(undefined)}>
                    Новый ключ
                  </Button>
                </div>
              </div>
            )}
          </Step>

          <Step n={n++} title="Что отправлять">
            <BodyEditor form={form} set={set} variables={variables} />
          </Step>
        </>
      )}

      <Step
        n={n++}
        title="Проверка"
        hint={kind === 'bitrix' ? 'Отправим тестовое событие через все включённые шаги.' : 'Отправим пример на ваш адрес и покажем ответ сервера.'}
      >
        <div className={s.condActions}>
          <Select
            value={testEvent}
            onChange={(e) => setTestEvent(e.target.value)}
            options={[
              { value: 'test.ping', label: 'Проверка связи' },
              ...catalog.events.filter((e) => e.type !== 'test.ping').map((e) => ({ value: e.type, label: e.title })),
            ]}
            style={{ maxWidth: 280 }}
          />
          <Button variant="secondary" onClick={doTest} loading={test.isPending}>
            <span className={s.btnRow}><PlayIcon /> Отправить тест</span>
          </Button>
        </div>
        {testResult && <DeliveryDetails delivery={testResult} compact />}
      </Step>
    </Drawer>
  )
}

function BodyEditor({
  form,
  set,
  variables,
}: {
  form: WebhookInput
  set: <K extends keyof WebhookInput>(k: K, v: WebhookInput[K]) => void
  variables: Catalog['events'][number]['variables']
}) {
  const { ref, insert } = useInsert<HTMLTextAreaElement>(form.bodyTemplate, (v) => set('bodyTemplate', v))
  const [preview, setPreview] = useState<string | null>(null)
  const prev = useAction(() => integrationsApi.previewTemplate(form.events[0] ?? 'call.completed', form.bodyTemplate))
  const starter = `{
  "phone": "{{data.contact.phone}}",
  "name": "{{data.contact.name}}",
  "result": "{{data.report.outcomeTitle}}",
  "summary": "{{data.report.summary}}",
  "recording": "{{data.call.recordingUrl}}"
}`
  return (
    <>
      <div className={s.choices}>
        <ChoiceCard active={form.bodyMode === 'standard'} onClick={() => set('bodyMode', 'standard')} title="Наш формат"
          text="Полный JSON события. Подходит, если пишете приёмник сами." />
        <ChoiceCard
          active={form.bodyMode === 'template'}
          onClick={() => {
            set('bodyMode', 'template')
            if (!form.bodyTemplate) set('bodyTemplate', starter)
          }}
          title="Свой шаблон"
          text="Соберите тело под формат вашей системы из готовых значений."
         
        />
      </div>
      {form.bodyMode === 'template' && (
        <>
          <textarea ref={ref} className={s.textarea} rows={9} value={form.bodyTemplate}
            onChange={(e) => set('bodyTemplate', e.target.value)} spellCheck={false} />
          <VariableChips variables={variables} onInsert={insert} />
          <div className={s.condActions}>
            <Button size="sm" variant="secondary" loading={prev.isPending}
              onClick={() => prev.mutate(undefined, { onSuccess: (r) => setPreview(r.body) })}>
              Посмотреть на настоящем звонке
            </Button>
          </div>
          {preview !== null && <JsonView value={preview} maxHeight={260} />}
        </>
      )}
    </>
  )
}

export function useOpenOAuthResult() {
  // Возврат с OAuth: /integrations?oauth=ok|error&message=…
  const params = new URLSearchParams(window.location.search)
  const st = params.get('oauth')
  if (st) {
    if (st === 'ok') toast.success('Аккаунт подключён')
    else toast.error(params.get('message') || 'Не удалось подключить аккаунт')
    window.history.replaceState(null, '', window.location.pathname)
  }
}
